
import React from "react";
import { Check } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Agent } from "@/models/agent";

interface AgentDetailsProps {
  agent: Agent;
}

export const AgentDetails: React.FC<AgentDetailsProps> = ({ agent }) => {
  return (
    <Tabs defaultValue="details" className="mb-8">
      <TabsList className="mb-4">
        <TabsTrigger value="details">Details</TabsTrigger>
        <TabsTrigger value="features">Features</TabsTrigger>
        <TabsTrigger value="use-cases">Use Cases</TabsTrigger>
        {agent.requirements && <TabsTrigger value="requirements">Requirements</TabsTrigger>}
      </TabsList>
      
      <TabsContent value="details" className="bg-white dark:bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">About this Agent</h2>
        <div className="prose dark:prose-invert max-w-none">
          {agent.fullDescription || agent.description}
        </div>
      </TabsContent>
      
      <TabsContent value="features" className="bg-white dark:bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Features</h2>
        {agent.features && agent.features.length > 0 ? (
          <ul className="space-y-3">
            {agent.features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-primary/20 text-primary flex items-center justify-center mr-3 mt-0.5">
                  <Check className="h-3 w-3" />
                </div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground">No features have been listed for this agent.</p>
        )}
      </TabsContent>
      
      <TabsContent value="use-cases" className="bg-white dark:bg-gray-800 rounded-xl p-6">
        <h2 className="text-xl font-semibold mb-4">Use Cases</h2>
        {agent.useCases && agent.useCases.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agent.useCases.map((useCase, index) => (
              <Card key={index}>
                <CardContent className="p-4">
                  <p>{useCase}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground">No use cases have been listed for this agent.</p>
        )}
      </TabsContent>
      
      {agent.requirements && (
        <TabsContent value="requirements" className="bg-white dark:bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold mb-4">Requirements</h2>
          <Card>
            <CardContent className="p-4">
              <p className="whitespace-pre-line">{agent.requirements}</p>
            </CardContent>
          </Card>
        </TabsContent>
      )}
    </Tabs>
  );
};
